/**
 * Strict public-configuration parsers for the advertising pixels. Exactly one
 * identifier field each, exact-shape objects only — anything else is dropped
 * before it can cross the server/client boundary. Mirrors google/config.ts.
 */

const META_PIXEL_ID = /^\d{15,16}$/;
const TIKTOK_PIXEL_CODE = /^[A-Z0-9]{16,24}$/;
const SNAP_PIXEL_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface MetaPixelPublicConfig {
  readonly pixelId: string;
}

export interface TikTokPixelPublicConfig {
  readonly pixelCode: string;
}

export interface SnapPixelPublicConfig {
  readonly pixelId: string;
}

export function parseMetaPixelPublicConfig(
  value: unknown,
): MetaPixelPublicConfig | null {
  if (!hasExactKeys(value, ['pixelId'])) {
    return null;
  }

  const pixelId = normalizeIdentifier(value.pixelId);

  return pixelId && META_PIXEL_ID.test(pixelId)
    ? Object.freeze({ pixelId })
    : null;
}

export function parseTikTokPixelPublicConfig(
  value: unknown,
): TikTokPixelPublicConfig | null {
  if (!hasExactKeys(value, ['pixelCode'])) {
    return null;
  }

  const pixelCode = normalizeIdentifier(value.pixelCode);

  return pixelCode && TIKTOK_PIXEL_CODE.test(pixelCode)
    ? Object.freeze({ pixelCode })
    : null;
}

export function parseSnapPixelPublicConfig(
  value: unknown,
): SnapPixelPublicConfig | null {
  if (!hasExactKeys(value, ['pixelId'])) {
    return null;
  }

  const pixelId = normalizeIdentifier(value.pixelId);

  return pixelId && SNAP_PIXEL_ID.test(pixelId)
    ? Object.freeze({ pixelId: pixelId.toLowerCase() })
    : null;
}

function hasExactKeys<Key extends string>(
  value: unknown,
  keys: readonly Key[],
): value is Record<Key, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value) as unknown;

  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }

  const actualKeys = Object.keys(value);

  return (
    actualKeys.length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function normalizeIdentifier(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  return normalized.length === value.length ? normalized : null;
}
